import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Nav,
  NavItem,
  NavLink,
  Row,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";
import Paginations from "../../components/Common/Pagination";
import Spinners from "../../components/Common/Spinner";
import {
  extractClientInvoiceRows,
  extractClientInvoiceTotal,
  useClientInvoicesQuery,
} from "../../api/client-invoices";
import {
  extractRefundRows,
  extractRefundTotal,
  useRefundMineQuery,
} from "../../api/refund";
import { useClientWallet } from "../../hooks/useClientWallet";
import { formatGbp } from "../../helpers/currency";
import { mediaUrl } from "../../helpers/mediaHelper";
import RefundDetailModal from "./RefundDetailModal";
import RefundRequestModal from "./RefundRequestModal";

const TEAL = "#00b3a0";

const MAIN_TABS = [
  { id: "invoices", label: "Invoices" },
  { id: "refunds", label: "Refunds" },
];

const REFUND_STATUS_COLOR = {
  pending: "warning",
  processing: "primary",
  approved: "success",
  completed: "success",
  rejected: "danger",
  cancelled: "secondary",
};

const REFUND_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "rejected", label: "Rejected" },
  { value: "completed", label: "Completed" },
];

const INVOICE_KIND_OPTIONS = [
  { value: "", label: "All kinds" },
  { value: "full", label: "Full" },
  { value: "upfront", label: "Upfront" },
  { value: "balance", label: "Balance" },
];

const INVOICE_PROVIDER_OPTIONS = [
  { value: "", label: "All providers" },
  { value: "stripe", label: "Stripe" },
  {
    value: "gocardless_instant_bank_payment",
    label: "GoCardless",
  },
];

const INVOICE_PAID_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Paid" },
  { value: "false", label: "Unpaid" },
];

function formatProvider(provider) {
  if (!provider) return "—";
  if (provider === "stripe") return "Stripe";
  if (provider === "gocardless_instant_bank_payment") return "GoCardless";
  return String(provider).replace(/_/g, " ");
}

const ClientPayments = () => {
  document.title = "Payments | Client";

  const wallet = useClientWallet();
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [detailRefund, setDetailRefund] = useState(null);
  const [activeTab, setActiveTab] = useState("invoices");

  const [invoicePage, setInvoicePage] = useState(1);
  const [invoiceLimit] = useState(10);
  const [invoiceKindFilter, setInvoiceKindFilter] = useState("full");
  const [providerFilter, setProviderFilter] = useState("");
  const [isPaidFilter, setIsPaidFilter] = useState("");

  const [refundPage, setRefundPage] = useState(1);
  const [refundLimit] = useState(10);
  const [statusFilter, setStatusFilter] = useState("pending");

  const invoiceListParams = useMemo(
    () => ({
      page: invoicePage,
      limit: invoiceLimit,
      invoiceKind: invoiceKindFilter,
      provider: providerFilter,
      isPaid: isPaidFilter,
    }),
    [invoicePage, invoiceLimit, invoiceKindFilter, providerFilter, isPaidFilter]
  );

  const refundListParams = useMemo(
    () => ({ page: refundPage, limit: refundLimit, status: statusFilter }),
    [refundPage, refundLimit, statusFilter]
  );

  const {
    data: invoiceData,
    isPending: invoicesPending,
    isError: invoicesError,
  } = useClientInvoicesQuery(invoiceListParams, {
    enabled: activeTab === "invoices",
  });

  const {
    data: refundData,
    isPending: refundsPending,
    isError: refundsError,
  } = useRefundMineQuery(refundListParams, {
    enabled: activeTab === "refunds",
  });

  const invoices = extractClientInvoiceRows(invoiceData);
  const invoiceTotal = extractClientInvoiceTotal(invoiceData);
  const refunds = extractRefundRows(refundData);
  const refundTotal = extractRefundTotal(refundData);

  const credit = Number(wallet?.credit ?? 0);
  const outstanding = Number(wallet?.outstandingBalance ?? 0);

  const creditDisplay = formatGbp(wallet?.credit ?? 0);
  const outstandingDisplay = formatGbp(wallet?.outstandingBalance ?? 0);

  const statusLabel =
    REFUND_STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label ??
    statusFilter;

  const invoiceColumns = useMemo(
    () => [
      {
        header: "Booking",
        accessorKey: "booking.slug",
        enableColumnFilter: false,
        cell: (info) => {
          const slug = info.getValue() || info.row.original.booking?.slug;
          if (!slug) return "—";
          return (
            <Link
              to={`/client/bookings/${slug}`}
              className="fw-semibold text-primary"
            >
              {slug}
            </Link>
          );
        },
      },
      {
        header: "Kind",
        accessorKey: "invoiceKind",
        enableColumnFilter: false,
        cell: (info) => {
          const kind = info.getValue();
          return kind ? <span className="text-capitalize">{kind}</span> : "—";
        },
      },
      {
        header: "Final amount",
        accessorKey: "finalAmount",
        enableColumnFilter: false,
        cell: (info) => formatGbp(info.getValue()),
      },
      {
        header: "Paid",
        accessorKey: "isPaid",
        enableColumnFilter: false,
        cell: (info) =>
          info.getValue() ? (
            <Badge className="bg-success">Paid</Badge>
          ) : (
            <Badge className="bg-warning text-dark">Unpaid</Badge>
          ),
      },
      {
        header: "Provider",
        accessorKey: "provider",
        enableColumnFilter: false,
        cell: (info) => formatProvider(info.getValue()),
      },
      {
        header: "Issue date",
        accessorKey: "InvoiceIssueDate",
        enableColumnFilter: false,
        cell: (info) => {
          const value = info.getValue();
          return value ? dayjs(value).format("DD MMM YYYY") : "—";
        },
      },
      {
        header: "Due date",
        accessorKey: "InvoiceDueDate",
        enableColumnFilter: false,
        cell: (info) => {
          const row = info.row.original;
          const date = row.InvoiceDueDate;
          const time = row.InvoiceDueTime;
          if (!date) return "—";
          const formatted = dayjs(date).format("DD MMM YYYY");
          return time ? `${formatted} ${time}` : formatted;
        },
      },
      {
        header: "Invoice",
        accessorKey: "invoiceURL",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => {
          const url = mediaUrl(info.getValue());
          if (!url) return "—";
          return (
            <a href={url} target="_blank" rel="noreferrer">
              View PDF
            </a>
          );
        },
      },
    ],
    []
  );

  const refundColumns = useMemo(
    () => [
      {
        header: "Refund ID",
        accessorKey: "slug",
        enableColumnFilter: false,
        cell: (info) => info.getValue() || "—",
      },
      {
        header: "Amount",
        accessorKey: "amount",
        enableColumnFilter: false,
        cell: (info) => formatGbp(info.getValue()),
      },
      {
        header: "Bank",
        accessorKey: "bankName",
        enableColumnFilter: false,
        cell: (info) => info.getValue() || "—",
      },
      {
        header: "Account holder",
        accessorKey: "accountHolderName",
        enableColumnFilter: false,
        cell: (info) => info.getValue() || "—",
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (info) => {
          const statusValue = info.getValue();
          const color = REFUND_STATUS_COLOR[statusValue] || "info";
          return (
            <Badge className={`bg-${color} text-uppercase`}>
              {statusValue || "unknown"}
            </Badge>
          );
        },
      },
      {
        header: "Verified",
        accessorKey: "isVerified",
        enableColumnFilter: false,
        cell: (info) =>
          info.getValue() ? (
            <Badge className="bg-success">Yes</Badge>
          ) : (
            <Badge className="bg-warning text-dark">Pending</Badge>
          ),
      },
      {
        header: "Requested",
        accessorKey: "createdAt",
        enableColumnFilter: false,
        cell: (info) =>
          info.getValue()
            ? dayjs(info.getValue()).format("DD MMM YYYY HH:mm")
            : "—",
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          const status = row.status;
          if (status !== "completed" && status !== "rejected") {
            return "—";
          }
          return (
            <Button
              color="primary"
              size="sm"
              outline
              onClick={() => setDetailRefund(row)}
            >
              View details
            </Button>
          );
        },
      },
    ],
    []
  );

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Client" breadcrumbItem="Payments" />

        <Row className="mb-4">
          <Col xs={12} className="d-flex justify-content-end mb-3 mb-lg-0">
            <Button
              color="primary"
              onClick={() => setRefundModalOpen(true)}
              disabled={credit <= 0}
            >
              <i className="bx bx-transfer-alt me-1" />
              Request refund
            </Button>
          </Col>
          <Col lg={6} md={6} className="my-3 mb-lg-0">
            <Card className="border-0 shadow-sm h-100 overflow-hidden">
              <div
                style={{
                  height: "4px",
                  background: `linear-gradient(90deg, ${TEAL}, #26c6b8)`,
                }}
              />
              <CardBody className="p-4">
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <p className="text-muted text-uppercase small mb-1 fw-semibold">
                      Available credit
                    </p>
                    <h3 className="mb-0 fw-bold" style={{ color: TEAL }}>
                      {creditDisplay}
                    </h3>
                    <p className="text-muted small mb-0 mt-2">
                      Credit notes from refunds and adjustments
                    </p>
                  </div>
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: 48,
                      height: 48,
                      backgroundColor: "rgba(0, 179, 160, 0.12)",
                      color: TEAL,
                    }}
                  >
                    <i className="bx bx-wallet font-size-24" />
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={6} md={6} className="my-3 mb-lg-0">
            <Card className="border-0 shadow-sm h-100 overflow-hidden">
              <div
                style={{
                  height: "4px",
                  background:
                    outstanding > 0
                      ? "linear-gradient(90deg, #f1b44c, #f46a6a)"
                      : "linear-gradient(90deg, #adb5bd, #ced4da)",
                }}
              />
              <CardBody className="p-4">
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <p className="text-muted text-uppercase small mb-1 fw-semibold">
                      Outstanding balance
                    </p>
                    <h3
                      className="mb-0 fw-bold"
                      style={{
                        color: outstanding > 0 ? "#f46a6a" : "#495057",
                      }}
                    >
                      {outstandingDisplay}
                    </h3>
                    <p className="text-muted small mb-0 mt-2">
                      Amount currently owed on your account
                    </p>
                  </div>
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: 48,
                      height: 48,
                      backgroundColor:
                        outstanding > 0
                          ? "rgba(244, 106, 106, 0.12)"
                          : "rgba(173, 181, 189, 0.2)",
                      color: outstanding > 0 ? "#f46a6a" : "#6c757d",
                    }}
                  >
                    <i className="bx bx-receipt font-size-24" />
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            <Card className="border-0 shadow-sm">
              <CardBody className="p-4">
                <Nav className="nav-tabs nav-tabs-custom mb-4">
                  {MAIN_TABS.map((tab) => (
                    <NavItem key={tab.id}>
                      <NavLink
                        href="#"
                        className={activeTab === tab.id ? "active" : ""}
                        onClick={(e) => {
                          e.preventDefault();
                          handleTabChange(tab.id);
                        }}
                      >
                        {tab.label}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>

                {activeTab === "invoices" ? (
                  <>
                    <Row className="g-2 mb-3 align-items-end">
                      <Col md={3} sm={6}>
                        <label className="form-label mb-1 text-muted small">
                          Invoice kind
                        </label>
                        <Input
                          type="select"
                          value={invoiceKindFilter}
                          onChange={(e) => {
                            setInvoiceKindFilter(e.target.value);
                            setInvoicePage(1);
                          }}
                        >
                          {INVOICE_KIND_OPTIONS.map((opt) => (
                            <option key={opt.value || "all"} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={3} sm={6}>
                        <label className="form-label mb-1 text-muted small">
                          Provider
                        </label>
                        <Input
                          type="select"
                          value={providerFilter}
                          onChange={(e) => {
                            setProviderFilter(e.target.value);
                            setInvoicePage(1);
                          }}
                        >
                          {INVOICE_PROVIDER_OPTIONS.map((opt) => (
                            <option key={opt.value || "all"} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={3} sm={6}>
                        <label className="form-label mb-1 text-muted small">
                          Payment status
                        </label>
                        <Input
                          type="select"
                          value={isPaidFilter}
                          onChange={(e) => {
                            setIsPaidFilter(e.target.value);
                            setInvoicePage(1);
                          }}
                        >
                          {INVOICE_PAID_OPTIONS.map((opt) => (
                            <option key={opt.value || "all"} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Input>
                      </Col>
                    </Row>

                    {invoicesPending && invoices.length === 0 ? (
                      <Spinners setLoading={() => {}} />
                    ) : invoicesError ? (
                      <div className="text-danger">
                        Failed to load invoices.
                      </div>
                    ) : invoices.length === 0 ? (
                      <div className="text-muted text-center py-4">
                        No invoices found.
                      </div>
                    ) : (
                      <>
                        <TableContainer
                          columns={invoiceColumns}
                          data={invoices}
                          isPagination={false}
                          isCustomPageSize={false}
                          isGlobalFilter={false}
                          isJobListGlobalFilter={false}
                          tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
                          theadClass="table-light"
                          divClassName="table-responsive"
                        />
                        {invoiceTotal > 0 && (
                          <Paginations
                            total={invoiceTotal}
                            perPageData={invoiceLimit}
                            currentPage={invoicePage}
                            setCurrentPage={setInvoicePage}
                            isShowingPageLength
                            paginationDiv="dataTables_paginate paging_simple_numbers pagination-rounded d-flex justify-content-end mt-3"
                            paginationClass="pagination"
                          />
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div className="d-flex flex-column mb-3">
                      <label className="form-label mb-1 text-muted small">
                        Status
                      </label>
                      <Input
                        type="select"
                        value={statusFilter}
                        onChange={(e) => {
                          setStatusFilter(e.target.value);
                          setRefundPage(1);
                        }}
                        style={{ maxWidth: 200 }}
                      >
                        {REFUND_STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Input>
                    </div>

                    {refundsPending && refunds.length === 0 ? (
                      <Spinners setLoading={() => {}} />
                    ) : refundsError ? (
                      <div className="text-danger">
                        Failed to load refund requests.
                      </div>
                    ) : refunds.length === 0 ? (
                      <div className="text-muted text-center py-4">
                        No {statusLabel.toLowerCase()} refund requests.
                      </div>
                    ) : (
                      <>
                        <TableContainer
                          columns={refundColumns}
                          data={refunds}
                          isPagination={false}
                          isCustomPageSize={false}
                          isGlobalFilter={false}
                          isJobListGlobalFilter={false}
                          tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
                          theadClass="table-light"
                          divClassName="table-responsive"
                        />
                        {refundTotal > 0 && (
                          <Paginations
                            total={refundTotal}
                            perPageData={refundLimit}
                            currentPage={refundPage}
                            setCurrentPage={setRefundPage}
                            isShowingPageLength
                            paginationDiv="dataTables_paginate paging_simple_numbers pagination-rounded d-flex justify-content-end mt-3"
                            paginationClass="pagination"
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col xs={12}>
            <Card className="border-0 shadow-sm">
              <CardBody className="p-4">
                <h5 className="mb-3">About your wallet</h5>
                <p className="text-muted mb-0">
                  Credit can be applied toward future bookings or withdrawn via
                  bank transfer. Refund requests are verified by email before
                  processing. Outstanding balance reflects unpaid invoices on
                  your account.
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      <RefundRequestModal
        isOpen={refundModalOpen}
        toggle={() => setRefundModalOpen(false)}
        maxAmount={credit}
      />

      <RefundDetailModal
        isOpen={Boolean(detailRefund)}
        toggle={() => setDetailRefund(null)}
        refund={detailRefund}
      />
    </div>
  );
};

export default ClientPayments;
